import { useEffect, useState } from 'react';
import { getImageUrl } from '../../utils/imageUtils';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Building2,
  Home,
  TreeDeciduous,
  Building
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypeIcons = {
  apartment: Building,
  villa: Home,
  land: TreeDeciduous,
  plot: TreeDeciduous,
  project: Building2,
  venture: Building2
};

const propertyTypeColors = {
  apartment: 'bg-blue-100 text-blue-700',
  villa: 'bg-green-100 text-green-700',
  land: 'bg-amber-100 text-amber-700',
  plot: 'bg-orange-100 text-orange-700',
  project: 'bg-purple-100 text-purple-700',
  venture: 'bg-teal-100 text-teal-700'
};

export default function PropertiesPage() {
  const { agency, getAuthHeader } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties`, {
        headers: getAuthHeader()
      });
      setProperties(response.data.properties);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(`${API_URL}/api/properties/${deleteId}`, {
        headers: getAuthHeader()
      });
      setProperties(prev => prev.filter(p => p.id !== deleteId));
      toast.success('Property deleted');
    } catch (error) {
      toast.error('Failed to delete property');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredProperties = properties.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location_city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location_area.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="flex justify-between">
            <div className="h-8 bg-navy-200 rounded w-1/4" />
            <div className="h-10 bg-navy-200 rounded w-32" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-72 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Properties</h1>
          <p className="text-navy-600">{properties.length} properties listed</p>
        </div>
        <Link to="/dashboard/properties/new">
          <Button className="bg-navy-900 hover:bg-navy-800 gap-2" data-testid="add-property-btn">
            <Plus className="w-4 h-4" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
        <Input
          placeholder="Search properties by name, city, or area..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-11"
          data-testid="search-input"
        />
      </div>

      {/* Properties Grid */}
      {filteredProperties.length === 0 ? (
        <Card className="py-16">
          <CardContent className="text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-navy-300" />
            <h3 className="text-lg font-semibold text-navy-900 mb-2">
              {searchQuery ? 'No properties found' : 'No properties yet'}
            </h3>
            <p className="text-navy-600 mb-6">
              {searchQuery
                ? 'Try a different search term'
                : 'Add your first property to get started'}
            </p>
            {!searchQuery && (
              <Link to="/dashboard/properties/new">
                <Button className="bg-navy-900 hover:bg-navy-800">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Property
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProperties.map((property) => {
            const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
            const typeColor = propertyTypeColors[property.property_type] || 'bg-gray-100 text-gray-700';

            return (
              <Card
                key={property.id}
                className="overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`property-card-${property.id}`}
              >
                <div className="relative h-48 bg-navy-100">
                  {property.images?.[0] ? (
                    <img
                      src={getImageUrl(property.images[0])}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <TypeIcon className="w-16 h-16 text-navy-300" />
                    </div>
                  )}
                  {property.featured && (
                    <Badge className="absolute top-3 left-3 bg-sky-500">Featured</Badge>
                  )}
                  {/* Banned/Blocked Badge */}
                  {(!property.is_active || property.status === 'blocked') && (
                    <Badge className="absolute top-3 left-3 bg-red-500 text-white z-10">Banned</Badge>
                  )}
                  <div className="absolute top-3 right-3">
                    <Badge className={typeColor}>
                      {property.property_type.charAt(0).toUpperCase() + property.property_type.slice(1)}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-navy-900 mb-1 truncate">{property.title}</h3>
                  <p className="text-sm text-navy-500 flex items-center gap-1 mb-3">
                    <MapPin className="w-4 h-4" />
                    {property.location_area}, {property.location_city}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-bold text-navy-900">
                      {property.property_type === 'venture' && property.price_min && property.price_max
                        ? `${formatPrice(property.price_min)} - ${formatPrice(property.price_max)}`
                        : formatPrice(property.price)
                      }
                    </span>
                    <span className="text-sm text-navy-500">
                      {property.size} {property.size_unit}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-navy-500 mb-4">
                    <Eye className="w-4 h-4" />
                    <span>{property.views} views</span>
                    {property.property_type === 'venture' && property.units_total > 0 && (
                      <span className="ml-auto text-xs">
                        <span className="text-green-600 font-medium">{property.units_available || 0}</span>
                        <span className="text-navy-400">/{property.units_total} plots available</span>
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/dashboard/properties/${property.id}/edit`} className="flex-1">
                      <Button variant="outline" className="w-full gap-2" data-testid={`edit-btn-${property.id}`}>
                        <Edit className="w-4 h-4" />
                        Edit
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteId(property.id)}
                      data-testid={`delete-btn-${property.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this property? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
